import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronLeft, ChevronRight, Columns3, Download, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, Undo2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MODULES, ENUM_LABELS, TERMINAL_FIELDS, formatMoney, formatDate } from "@/modules/config";
import { RecordDialog } from "@/components/RecordDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/i18n/LanguageContext";

const STATUS_STYLES = {
  green: ["odendi", "tahsil_edildi", "aktif", "hesaba_gecti"],
  orange: ["bekliyor", "portfoyde", "bankada", "bankaya_verildi", "ciro_edildi", "verildi", "odenmedi", "tahsil_edilecek", "odenecek", "planlandi"],
  red: ["gecikti", "iptal", "iade", "karsiliksiz", "kapali"],
};

function statusClass(value) {
  if (STATUS_STYLES.green.includes(value)) return "bg-green-100 text-green-800 border-green-200";
  if (STATUS_STYLES.orange.includes(value)) return "bg-amber-100 text-amber-800 border-amber-200";
  if (STATUS_STYLES.red.includes(value)) return "bg-red-100 text-red-800 border-red-200";
  return "border-slate-300 text-slate-700";
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function CellValue({ col, value, record, highlightToday = true }) {
  const { t } = useLang();
  if (col.type === "money") {
    const cur = record?.currency;
    const tlVal = record?.[`${col.key}_tl`];
    const dirCls = record?.direction === "giris" ? " text-green-600 font-medium"
      : record?.direction === "cikis" ? " text-red-600 font-medium" : "";
    return (
      <span className={`tabular${dirCls}`}>
        {formatMoney(value, cur)}
        {cur && cur !== "TL" && tlVal != null && (
          <span className="text-slate-400 text-xs" data-testid={`tl-equiv-${record.id}-${col.key}`}>
            {" "}({formatMoney(tlVal)})
          </span>
        )}
      </span>
    );
  }
  if (col.type === "date") {
    const isToday = highlightToday && value && String(value).slice(0, 10) === todayStr();
    return (
      <span className={`tabular ${isToday ? "bg-amber-200 text-amber-900 font-semibold px-1.5 py-0.5 rounded" : ""}`}
        data-testid={isToday ? "date-today" : undefined}>
        {formatDate(value)}
      </span>
    );
  }
  if (col.type === "enum")
    return value ? (
      <Badge variant="outline" className={statusClass(value)}>
        {t(ENUM_LABELS[value] || value)}
      </Badge>
    ) : (
      "—"
    );
  return value ?? "—";
}

function StatCards({ config, summary, records }) {
  const { t } = useLang();
  if (!summary || !config.stats) return null;
  const totals = summary.totals || {};
  const sumFor = (arr) => arr.reduce((a, s) => a + (totals[s]?.total || 0), 0);
  const countFor = (arr) => arr.reduce((a, s) => a + (totals[s]?.count || 0), 0);
  let cards;
  if (config.stats.custom === "received") {
    const rows = records || [];
    const totalAmt = rows.reduce((a, r) => a + (r.amount_tl ?? r.amount ?? 0), 0);
    const dayNum = (s) => {
      const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
      return Math.round(Date.UTC(y, m - 1, d) / 86400000);
    };
    const todayTR = dayNum(new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" }));
    const dated = rows.filter((r) => r.due_date && (r.amount_tl ?? r.amount ?? 0) > 0);
    let avgLabel = "—";
    if (dated.length) {
      const wsum = dated.reduce((a, r) =>
        a + (dayNum(r.due_date) - todayTR) * (r.amount_tl ?? r.amount), 0);
      const wtot = dated.reduce((a, r) => a + (r.amount_tl ?? r.amount), 0);
      const avgDays = Math.round(wsum / wtot);
      const avgDate = new Date((todayTR + avgDays) * 86400000).toISOString().slice(0, 10);
      avgLabel = `${avgDays} ${t("gün")} (${formatDate(avgDate)})`;
    }
    cards = [
      { label: t("Genel Toplam (₺)"), value: formatMoney(totalAmt), cls: "text-[#0A1128]" },
      { label: t("Ortalama Vade"), value: avgLabel, cls: "text-blue-700" },
    ];
  } else if (config.stats.custom === "pos") {
    cards = [
      { label: t("Toplam İşlem"), value: summary.count, cls: "text-[#0A1128]" },
      { label: t("Brüt Toplam (₺)"), value: formatMoney(summary.gross_total || 0), cls: "text-[#0A1128]" },
      { label: t("Komisyon (₺)"), value: formatMoney(summary.commission_total || 0), cls: "text-amber-600" },
      { label: t("Hesabınıza Geçen Net (₺)"), value: formatMoney(totals.genel?.total || 0), cls: "text-green-600" },
    ];
  } else {
    const s = config.stats;
    const pendingSum = sumFor(s.pendingStatuses);
    const doneSum = sumFor(s.doneStatuses);
    cards = [{ label: t("Toplam Kayıt"), value: summary.count, cls: "text-[#0A1128]" }];
    if (s.pendingStatuses.length)
      cards.push({ label: t(s.pendingLabel), value: formatMoney(pendingSum), sub: `${countFor(s.pendingStatuses)} ${t("adet")}`, cls: s.pendingCls || "text-amber-600" });
    if (s.doneStatuses.length)
      cards.push({ label: t(s.doneLabel), value: formatMoney(doneSum), sub: `${countFor(s.doneStatuses)} ${t("adet")}`, cls: s.doneCls || "text-green-600" });
    const grand = s.grand === "bakiye"
      ? pendingSum - doneSum
      : Object.values(totals).reduce((a, t) => a + t.total, 0);
    cards.push({ label: t(s.grand === "bakiye" ? "Bakiye (₺)" : "Genel Toplam (₺)"), value: formatMoney(grand), cls: "text-[#0A1128]" });
  }
  return (
    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
      {cards.map((c) => (
        <div key={c.label} className="border border-slate-200 bg-white rounded-md p-5 fade-up">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">{c.label}</p>
          <p className={`mt-2 text-xl lg:text-2xl font-semibold tabular ${c.cls}`}>{c.value}</p>
          {c.sub && <p className="text-xs text-slate-400 mt-1">{c.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function formatMonth(m) {
  try {
    return new Date(`${m}-01T00:00:00`).toLocaleDateString("tr-TR", { year: "numeric", month: "long" });
  } catch {
    return m;
  }
}

function MonthlyTable({ monthly }) {
  const { t } = useLang();
  if (!monthly) {
    return (
      <div className="p-12 flex justify-center" data-testid="monthly-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (monthly.length === 0) {
    return <p className="p-12 text-sm text-slate-500" data-testid="monthly-empty">{t("Henüz hareket yok.")}</p>;
  }
  return (
    <Table data-testid="monthly-table">
      <TableHeader>
        <TableRow className="bg-slate-50">
          <TableHead>{t("Ay")}</TableHead>
          <TableHead>{t("İşlem")}</TableHead>
          <TableHead>{t("Brüt")}</TableHead>
          <TableHead>{t("Komisyon")}</TableHead>
          <TableHead>{t("Net")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {monthly.map((m) => (
          <TableRow key={m.month} className="hover:bg-slate-50" data-testid={`monthly-row-${m.month}`}>
            <TableCell className="font-medium text-[#0A1128]">{formatMonth(m.month)}</TableCell>
            <TableCell className="tabular text-slate-600">{m.count}</TableCell>
            <TableCell className="tabular text-slate-600">{formatMoney(m.gross)}</TableCell>
            <TableCell className="tabular text-amber-600">{formatMoney(m.commission)}</TableCell>
            <TableCell className="tabular text-green-600 font-medium">{formatMoney(m.net)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TerminalManager({ canWrite }) {
  const { t } = useLang();
  const [terminals, setTerminals] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const cfg = { singular: "POS Terminali", endpoint: "pos_terminals", fields: TERMINAL_FIELDS };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/pos_terminals");
      setTerminals(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setTerminals([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    try {
      await api.delete(`/finance/pos_terminals/${deleting.id}`);
      toast.success("Terminal silindi");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="terminals-tab">
      <div className="flex justify-end mb-4">
        {canWrite && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-terminal-button"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Terminal
          </Button>
        )}
      </div>
      <div className="border border-slate-200 bg-white rounded-md overflow-x-auto">
        {terminals === null ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : terminals.length === 0 ? (
          <p className="p-12 text-sm text-slate-500" data-testid="terminals-empty">
            {t("Henüz terminal yok. Terminal eklediğinizde komisyon oranı hareket formunda otomatik gelir.")}
          </p>
        ) : (
          <Table data-testid="terminals-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("Terminal")}</TableHead>
                <TableHead>{t("Banka")}</TableHead>
                <TableHead>{t("Şirket")}</TableHead>
                <TableHead>{t("Komisyon %")}</TableHead>
                <TableHead>{t("Notlar")}</TableHead>
                {canWrite && <TableHead className="text-right">{t("İşlem")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {terminals.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`terminal-row-${t.id}`}>
                  <TableCell className="font-medium text-[#0A1128]">{t.name}</TableCell>
                  <TableCell className="text-slate-600">{t.bank || "—"}</TableCell>
                  <TableCell className="text-slate-600">{t.company || "—"}</TableCell>
                  <TableCell className="tabular text-slate-600">%{t.commission_rate ?? 0}</TableCell>
                  <TableCell className="text-slate-600">{t.notes || "—"}</TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" aria-label="Düzenle"
                          onClick={() => { setEditing(t); setOpen(true); }}
                          data-testid={`edit-terminal-${t.id}`}
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" aria-label="Sil"
                          onClick={() => setDeleting(t)}
                          data-testid={`delete-terminal-${t.id}`}
                          className="text-slate-500 hover:text-red-600 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <RecordDialog open={open} onOpenChange={setOpen} config={cfg} record={editing} onSaved={load} />
      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="terminal-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Terminali silmek istediğinize emin misiniz?")}</AlertDialogTitle>
            <AlertDialogDescription>{t("Bu işlem geri alınamaz.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="terminal-delete-cancel">{t("Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="terminal-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white">
              {t("Sil")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function ModulePage({ moduleKey }) {
  const config = MODULES[moduleKey];
  const { user } = useAuth();
  const { t } = useLang();
  const canWrite = ["admin", "editor"].includes(user?.role);
  const isPos = config.custom === "pos";
  const [records, setRecords] = useState(null);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({ search: "", company: "all", status: "all", method: "all", dateFrom: "", dateTo: "" });
  const [selected, setSelected] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteMode, setDeleteMode] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState("hareketler");
  const [monthly, setMonthly] = useState(null);
  const [transferState, setTransferState] = useState(null);
  const [transferForm, setTransferForm] = useState({ transfer_date: "", target_info: "", notes: "" });
  const [revertState, setRevertState] = useState(null);
  const [sort, setSort] = useState(null);
  const [categories, setCategories] = useState([]);

  const statusField = config.fields.find((f) => f.key === "status");
  const methodField = config.fields.find((f) => f.key === "method");
  const categoryField = config.fields.find((f) => f.key === "category");
  const hasDate = config.fields.some((f) => f.type === "date");

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.company !== "all") params.company = filters.company;
    if (filters.method !== "all") params.method = filters.method;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    return params;
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: sum }, { data: filt }] = await Promise.all([
        api.get(`/finance/${config.endpoint}`, { params: buildParams() }),
        api.get(`/finance/${config.endpoint}/summary`),
        api.get(`/finance/${config.endpoint}/filters`),
      ]);
      setRecords(list);
      setSummary(sum);
      setCompanies(filt.companies || []);
      setCategories(filt.categories || []);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
      setRecords([]);
    }
  }, [config.endpoint, buildParams]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isPos && tab === "aylik") {
      setMonthly(null);
      api.get(`/finance/${config.endpoint}/monthly`).then(({ data }) => setMonthly(data)).catch(() => setMonthly([]));
    }
  }, [isPos, tab, config.endpoint, records]);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!records) return;
    setSelected((s) => (s.size === records.length ? new Set() : new Set(records.map((r) => r.id))));
  };

  const handleDeleteConfirm = async () => {
    try {
      if (deleteMode.type === "single") {
        await api.delete(`/finance/${config.endpoint}/${deleteMode.record.id}`);
        toast.success("Kayıt silindi");
      } else if (deleteMode.type === "all") {
        const { data } = await api.post(`/finance/${config.endpoint}/bulk-delete`, { all: true });
        toast.success(`${data.deleted} kayıt silindi`);
      } else {
        const { data } = await api.post(`/finance/${config.endpoint}/bulk-delete`, { ids: [...selected] });
        toast.success(`${data.deleted} kayıt silindi`);
      }
      setDeleteMode(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportExcel = async () => {
    try {
      const { data } = await api.get(`/finance/${config.endpoint}/export`, {
        params: buildParams(), responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${config.endpoint}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("Excel indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportPdf = async () => {
    try {
      const { data } = await api.get(`/finance/${config.endpoint}/export.pdf`, {
        params: buildParams(), responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${config.endpoint}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("PDF indirildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openTransfer = (ids, target) => {
    const first = records?.find((r) => r.id === ids[0]);
    setTransferForm({
      transfer_date: todayStr(),
      target_info: target === "bank" ? (first?.bank || "") : "",
      notes: "",
      expected_date: target === "bank" ? (first?.due_date || "") : "",
    });
    setTransferState({ ids, target });
  };

  const submitTransfer = async () => {
    const { ids, target } = transferState;
    try {
      const payload = {
        ids,
        target,
        transfer_date: transferForm.transfer_date || undefined,
        notes: transferForm.notes || undefined,
      };
      if (target === "bank") {
        payload.bank = transferForm.target_info || undefined;
        if (transferForm.expected_date) payload.expected_collection_date = transferForm.expected_date;
      } else {
        payload.endorsed_to = transferForm.target_info || undefined;
      }
      const { data } = await api.post(`/finance/${config.endpoint}/transfer`, payload);
      toast.success(`${data.moved} ${t("çek aktarıldı")}`);
      setTransferState(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitRevert = async () => {
    try {
      const { data } = await api.post(`/finance/${config.endpoint}/revert`, { ids: revertState.ids });
      toast.success(`${data.moved} ${t("çek geri alındı")}`);
      setRevertState(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/finance/${config.endpoint}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${config.endpoint}_sablon.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/finance/${config.endpoint}/import`, fd);
      setImportResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} kayıt aktarıldı`);
        load();
      }
      if (data.errors?.length) toast.error(`${data.errors.length} satır aktarılamadı`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const deleteTexts = {
    single: t("Bu kayıt kalıcı olarak silinecek."),
    all: t("Bu modüldeki TÜM kayıtlar kalıcı olarak silinecek."),
    selected: `${selected.size} ${t("kayıt kalıcı olarak silinecek.")}`,
  };

  const toggleSort = (key) => {
    setSort((s) => (!s || s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));
  };

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = window.localStorage.getItem(`temha-cols-${moduleKey}`);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) return arr;
      }
    } catch { /* depolama engelli ortamlarda varsayilan */ }
    return null;
  });
  const activeColumns = config.columns.filter((c) => !visibleCols || visibleCols.includes(c.key));
  const toggleCol = (key) => {
    setVisibleCols((cur) => {
      const base = cur ?? config.columns.map((c) => c.key);
      const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
      if (next.length === 0) return cur;
      try { window.localStorage.setItem(`temha-cols-${moduleKey}`, JSON.stringify(next)); } catch { }
      return next;
    });
  };

  const [pageSize, setPageSize] = useState(() => {
    try {
      const raw = Number(window.localStorage.getItem("temha-page-size"));
      if ([50, 100, 500].includes(raw)) return raw;
    } catch { }
    return 100;
  });
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filters, moduleKey, pageSize]);
  const changePageSize = (v) => {
    const n = Number(v);
    setPageSize(n);
    try { window.localStorage.setItem("temha-page-size", String(n)); } catch { }
  };

  const displayRecords = (() => {
    if (!records || !sort) return records;
    const col = config.columns.find((c) => c.key === sort.key);
    const arr = [...records];
    arr.sort((a, b) => {
      let va = a[sort.key];
      let vb = b[sort.key];
      if (col?.type === "money") {
        va = va == null || va === "" ? null : Number(va);
        vb = vb == null || vb === "" ? null : Number(vb);
      } else {
        va = va == null || va === "" ? null : String(col?.type === "enum" ? (ENUM_LABELS[va] || va) : va);
        vb = vb == null || vb === "" ? null : String(col?.type === "enum" ? (ENUM_LABELS[vb] || vb) : vb);
      }
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = typeof va === "number" ? va - vb : va.localeCompare(vb, "tr");
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  })();

  const totalPages = Math.max(1, Math.ceil((displayRecords?.length ?? 0) / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRecords = displayRecords ? displayRecords.slice((safePage - 1) * pageSize, safePage * pageSize) : displayRecords;

  const recordsTable = (
    <>
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder={t(config.searchPlaceholder)}
            data-testid="module-search-input"
            className="pl-9 focus-visible:ring-blue-600 bg-white"
          />
        </div>
        <Select value={filters.company} onValueChange={(v) => setFilters((f) => ({ ...f, company: v }))}>
          <SelectTrigger className="w-44 bg-white focus:ring-blue-600" data-testid="company-filter">
            <SelectValue placeholder={t("Şirket")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="company-filter-all">{t("Tüm Şirketler")}</SelectItem>
            {companies.map((c) => (
              <SelectItem key={c} value={c} data-testid={`company-filter-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {statusField && (
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-44 bg-white focus:ring-blue-600" data-testid="module-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="status-filter-all">{t("Tüm Durumlar")}</SelectItem>
              {statusField.options.map((o) => (
                <SelectItem key={o} value={o} data-testid={`status-filter-${o}`}>
                  {t(ENUM_LABELS[o] || o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {methodField && (
          <Select value={filters.method} onValueChange={(v) => setFilters((f) => ({ ...f, method: v }))}>
            <SelectTrigger className="w-44 bg-white focus:ring-blue-600" data-testid="method-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="method-filter-all">{t("Tüm Ödeme Şekilleri")}</SelectItem>
              {methodField.options.map((o) => (
                <SelectItem key={o} value={o} data-testid={`method-filter-${o}`}>
                  {t(ENUM_LABELS[o] || o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {categoryField && (
          <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}>
            <SelectTrigger className="w-44 bg-white focus:ring-blue-600" data-testid="category-filter">
              <SelectValue placeholder={t("Kategori")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="category-filter-all">{t("Tüm Kategoriler")}</SelectItem>
              {(categoryField.type === "enum" ? categoryField.options : categories).map((c) => (
                <SelectItem key={c} value={c} data-testid={`category-filter-${c}`}>
                  {t(ENUM_LABELS[c] || c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {hasDate && (
          <>
            <Input type="date" value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              data-testid="date-from-filter" className="w-40 bg-white focus-visible:ring-blue-600" />
            <Input type="date" value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              data-testid="date-to-filter" className="w-40 bg-white focus-visible:ring-blue-600" />
          </>
        )}
      </div>

      {selected.size > 0 && canWrite && (
        <div className="mt-4 flex items-center gap-4 flex-wrap rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid="selection-bar">
          <span className="text-sm text-blue-800">{selected.size} {t("kayıt seçildi")}</span>
          {config.transferActions?.map((a) => (
            <Button key={a.key} size="sm" variant="outline" onClick={() => openTransfer([...selected], a.target)}
              data-testid={`bulk-transfer-${a.target}`}
              className="border-blue-300 text-blue-700 hover:bg-blue-100">
              {a.key} — {t(a.label)}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => setDeleteMode({ type: "selected" })}
            data-testid="bulk-delete-button"
            className="border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {t("Seçilenleri Sil")}
          </Button>
        </div>
      )}

      <div className="mt-4 border border-slate-200 bg-white rounded-md overflow-x-auto">
        {records === null ? (
          <div className="p-12 flex justify-center" data-testid="module-loading">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : records.length === 0 ? (
          <p className="p-12 text-sm text-slate-500" data-testid="module-empty">
            {t("Henüz kayıt yok.")} {canWrite ? `${t("Yeni")} ${t(config.singular)} — ${t("ilk kaydı ekleyin")}` : ""}
          </p>
        ) : (
          <Table data-testid="module-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {canWrite && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={records.length > 0 && selected.size === records.length}
                      onCheckedChange={toggleAll}
                      aria-label={t("Tümünü seç")}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                )}
                {activeColumns.map((c) => (
                  <TableHead key={c.key}>
                    <button type="button" onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}
                      className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors duration-150">
                      {t(c.label)}
                      {sort?.key === c.key
                        ? (sort.dir === "asc"
                          ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
                          : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />)
                        : <ArrowUpDown className="h-3 w-3 text-slate-300" />}
                    </button>
                  </TableHead>
                ))}
                {canWrite && <TableHead className="text-right">{t("İşlem")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRecords.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`record-row-${r.id}`}>
                  {canWrite && (
                    <TableCell>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                        aria-label="Kaydı seç"
                        data-testid={`row-select-${r.id}`}
                      />
                    </TableCell>
                  )}
                  {activeColumns.map((c, i) => (
                    <TableCell key={c.key} className={i === 0 ? "font-medium text-[#0A1128]" : "text-slate-600"}>
                      <CellValue col={c} value={r[c.key]} record={r} highlightToday={!config.noTodayHighlight} />
                    </TableCell>
                  ))}
                  {canWrite && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {config.transferActions?.map((a) => (
                          <button
                            key={a.key}
                            type="button"
                            title={t(a.label)}
                            onClick={() => openTransfer([r.id], a.target)}
                            data-testid={`transfer-${a.target}-${r.id}`}
                            className="h-8 w-8 rounded-md border border-slate-300 text-xs font-bold text-slate-600 hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200"
                          >
                            {a.key}
                          </button>
                        ))}
                        {config.revertAction && (
                          <button
                            type="button"
                            title={t("Geri Al")}
                            onClick={() => setRevertState({ ids: [r.id] })}
                            data-testid={`revert-${r.id}`}
                            className="h-8 w-8 rounded-md border border-slate-300 text-slate-600 hover:border-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors duration-200 inline-flex items-center justify-center"
                          >
                            <Undo2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <Button
                          variant="ghost" size="icon" aria-label={t("Düzenle")}
                          onClick={() => { setEditing(r); setDialogOpen(true); }}
                          data-testid={`edit-record-${r.id}`}
                          className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" aria-label={t("Sil")}
                          onClick={() => setDeleteMode({ type: "single", record: r })}
                          data-testid={`delete-record-${r.id}`}
                          className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {displayRecords && (
          <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-slate-100" data-testid="pagination-bar">
            <span className="text-xs text-slate-400" data-testid="pagination-total">
              {displayRecords.length} {t("kayıt")}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">{t("Sayfa başına:")}</span>
              <Select value={String(pageSize)} onValueChange={changePageSize}>
                <SelectTrigger className="h-8 w-20 bg-white text-xs" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[50, 100, 500].map((n) => (
                    <SelectItem key={n} value={String(n)} data-testid={`page-size-${n}`}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" disabled={safePage <= 1} aria-label={t("Önceki")}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev" className="h-8 w-8 border-slate-300 text-slate-600">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs tabular text-slate-500" data-testid="page-info">{safePage} / {totalPages}</span>
              <Button variant="outline" size="icon" disabled={safePage >= totalPages} aria-label={t("İleri")}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="page-next" className="h-8 w-8 border-slate-300 text-slate-600">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="fade-up" data-testid={`module-page-${moduleKey}`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t(config.title)}</h1>
          <p className="mt-2 text-slate-500">{config.subtitle ? t(config.subtitle) : `${summary?.count ?? 0} ${t("kayıt")}`}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-button"
            className="border-slate-300 text-slate-700 hover:bg-slate-100">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("Yenile")}
          </Button>
          {canWrite && (
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}
              data-testid="import-button" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              <Upload className="h-4 w-4 mr-2" />
              {t("İçe Aktar")}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="columns-button"
                className="border-slate-300 text-slate-700 hover:bg-slate-100">
                <Columns3 className="h-4 w-4 mr-2" />
                {t("Kolonlar")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {config.columns.map((c) => {
                const active = !visibleCols || visibleCols.includes(c.key);
                return (
                  <DropdownMenuItem key={c.key} onSelect={(e) => { e.preventDefault(); toggleCol(c.key); }}
                    data-testid={`col-toggle-${c.key}`} className="flex items-center gap-2">
                    <span className={`h-4 w-4 rounded border inline-flex items-center justify-center ${
                      active ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"
                    }`}>
                      {active && <Check className="h-3 w-3" />}
                    </span>
                    {t(c.label)}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-button"
                className="border-slate-300 text-slate-700 hover:bg-slate-100">
                <Download className="h-4 w-4 mr-2" />
                {t("Dışa Aktar")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportExcel} data-testid="export-excel-item">
                {t("Excel olarak indir")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPdf} data-testid="export-pdf-item">
                {t("PDF olarak indir")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canWrite && (
            <Button variant="outline" onClick={() => setDeleteMode({ type: "all" })}
              data-testid="delete-all-button"
              className="border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" />
              {t("Tümünü Sil")}
            </Button>
          )}
          {canWrite && (
            <Button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              data-testid="new-record-button"
              className="bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 hover:-translate-y-[1px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("Yeni")} {t(config.singular)}
            </Button>
          )}
        </div>
      </div>

      <StatCards config={config} summary={summary} records={records} />

      {isPos && (
        <div className="mt-8 flex gap-1 border-b border-slate-200" data-testid="pos-tabs">
          {[
            ["hareketler", `${t("Hareketler")} (${summary?.count ?? 0})`, "tab-hareketler"],
            ["aylik", t("Aylık Özet"), "tab-aylik-ozet"],
            ["poslar", t("POS'lar"), "tab-poslar"],
          ].map(([key, label, tid]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={tid}
              className={`px-4 py-2.5 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px ${
                tab === key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-[#0A1128]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isPos && tab === "aylik" ? (
        <div className="mt-4 border border-slate-200 bg-white rounded-md overflow-x-auto">
          <MonthlyTable monthly={monthly} />
        </div>
      ) : isPos && tab === "poslar" ? (
        <div className="mt-4">
          <TerminalManager canWrite={canWrite} />
        </div>
      ) : (
        recordsTable
      )}

      <RecordDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        config={config}
        record={editing}
        onSaved={load}
      />

      <Dialog open={Boolean(transferState)} onOpenChange={(o) => !o && setTransferState(null)}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0A1128]">
              {transferState?.target === "bank" ? t("Bankaya Aktar") : t("Ciro Et")}
              {transferState ? ` (${transferState.ids.length} ${t("çek")})` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="transfer-date">{t("Tarih")}</Label>
              <Input id="transfer-date" type="date" value={transferForm.transfer_date}
                onChange={(e) => setTransferForm((f) => ({ ...f, transfer_date: e.target.value }))}
                data-testid="transfer-date-input" className="focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="transfer-target">
                {transferState?.target === "bank" ? t("Verilen Banka") : t("Verilen Firma")}
              </Label>
              <Input id="transfer-target" value={transferForm.target_info}
                onChange={(e) => setTransferForm((f) => ({ ...f, target_info: e.target.value }))}
                data-testid="transfer-target-input" className="focus-visible:ring-blue-600" />
            </div>
            {transferState?.target === "bank" && (
              <div className="space-y-2">
                <Label htmlFor="transfer-expected-date">{t("Tahmini Tahsil Tarihi")}</Label>
                <Input id="transfer-expected-date" type="date" value={transferForm.expected_date}
                  onChange={(e) => setTransferForm((f) => ({ ...f, expected_date: e.target.value }))}
                  data-testid="transfer-expected-date-input" className="focus-visible:ring-blue-600" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="transfer-notes">{t("Notlar")}</Label>
              <Textarea id="transfer-notes" rows={3} value={transferForm.notes}
                onChange={(e) => setTransferForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="transfer-notes-input" className="focus-visible:ring-blue-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferState(null)} data-testid="transfer-cancel-button">
              {t("Vazgeç")}
            </Button>
            <Button onClick={submitTransfer} data-testid="transfer-submit-button"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {t("Aktar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(revertState)} onOpenChange={(o) => !o && setRevertState(null)}>
        <AlertDialogContent data-testid="revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Geri Al")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Çek, Alınan Çekler sayfasına geri aktarılacak.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revert-cancel-button">{t("Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction onClick={submitRevert} data-testid="revert-confirm-button"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {t("Geri Al")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteMode)} onOpenChange={(o) => !o && setDeleteMode(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Silmek istediğinize emin misiniz?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMode ? deleteTexts[deleteMode.type] : ""} {t("Bu işlem geri alınamaz.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">{t("Vazgeç")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              data-testid="delete-confirm-button"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t("Sil")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0A1128]">{t("Excel ile İçe Aktar")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {t("Şablonu indirin, doldurun ve yükleyin. Sütun başlıkları değiştirilmemelidir.")}
            </p>
            <Button variant="outline" onClick={downloadTemplate} data-testid="import-template-button"
              className="border-slate-300 text-slate-700 hover:bg-slate-100">
              <Download className="h-4 w-4 mr-2" />
              {t("Şablon İndir")}
            </Button>
            <div className="space-y-2">
              <Label htmlFor="import-file">{t("Excel Dosyası")}</Label>
              <Input id="import-file" type="file" accept=".xlsx,.csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                data-testid="import-file-input" className="focus-visible:ring-blue-600" />
            </div>
            {importResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="import-result">
                <p className="text-green-700 font-medium">{importResult.inserted} {t("kayıt aktarıldı")}</p>
                {importResult.errors?.slice(0, 5).map((e) => (
                  <p key={e} className="text-red-600 text-xs mt-1">{e}</p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel-button">
              {t("Kapat")}
            </Button>
            <Button onClick={submitImport} disabled={!importFile || importing} data-testid="import-submit-button"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Aktar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
